import { Keys } from '../../config/keys';
import { EStreamTradeDTO } from '../../types/estream/eStreamTrade';
import { InstrumentStoreItemDetails } from '../../types/estream/instrumentStoreItem';
import { InstrumentCategoryEnum, ListedInstrument } from '../../types/instrument/listedInstrument';
import { isDefined, when } from '../../utils/objectUtils';

import { isUsaPremarketTime } from '../../utils';
import { betMarketCodes, usaMarketCodes, xetraFrMarketCodes } from './eStreamMapperConsts';

export const mapDetails = (
  listedInstrument: ListedInstrument | undefined,
  item: EStreamTradeDTO,
  ric: string,
): Partial<InstrumentStoreItemDetails> => {
  const instrumentCategory = listedInstrument?.instrumentCategory as InstrumentCategoryEnum | undefined;
  const mic = listedInstrument?.mic || '';
  const high52Week = when(usaMarketCodes.has(mic), item?.data?.['52WK_HIGH'])
    .or(
      betMarketCodes.has(mic) ||
        xetraFrMarketCodes.has(mic) ||
        ric === Keys.Instruments.Ric.BUX ||
        (!!instrumentCategory && [InstrumentCategoryEnum.Currency].includes(instrumentCategory)),
      item?.data?.YRHIGH,
    )
    .get();

  const low52Week = when(usaMarketCodes.has(mic), item?.data?.['52WK_LOW'])
    .or(
      betMarketCodes.has(mic) ||
        xetraFrMarketCodes.has(mic) ||
        ric === Keys.Instruments.Ric.BUX ||
        (!!instrumentCategory && [InstrumentCategoryEnum.Currency].includes(instrumentCategory)),
      item?.data?.YRLOW,
    )
    .get();

  const middlePrice = when(xetraFrMarketCodes.has(mic), item?.data?.MID_PRICE)
    .or(
      isDefined(item?.data?.BID) && isDefined(item?.data?.ASK) && item?.data?.BID !== 'M' && item?.data?.ASK !== 'M',
      ((item?.data?.BID as number) + (item?.data?.ASK as number)) / 2,
    )
    .get();

  const yearToDateChange = when(
    listedInstrument?.instrumentCategory === InstrumentCategoryEnum.Equity,
    item?.data?.PRYTDPCT,
  )
    .or(ric === Keys.Instruments.Ric.DAX || ric === Keys.Instruments.Ric.BUX, item?.data?.YR_PCTCH)
    .get();

  const lowDate52Week = when(
    listedInstrument?.instrumentCategory === InstrumentCategoryEnum.ETF,
    item?.data?.['52W_LDAT'],
  )
    .or(listedInstrument?.instrumentCategory === InstrumentCategoryEnum.Index, item?.data?.YRLOWDAT)
    .get();

  const highDate52Week = when(
    listedInstrument?.instrumentCategory === InstrumentCategoryEnum.ETF,
    item?.data?.['52W_HDAT'],
  )
    .or(listedInstrument?.instrumentCategory === InstrumentCategoryEnum.Index, item?.data?.YRHIGHDAT)
    .get();

  const isFutures = instrumentCategory === InstrumentCategoryEnum.Futures;
  const vwap = isFutures ? item?.data?.VWAP_EXCH : item?.data?.VWAP;

  const isPremarket = usaMarketCodes.has(mic) && isUsaPremarketTime();

  const volumeQuantity =
    when<number | undefined>(isDefined(item?.data?.ACVOL_PRE) && !isPremarket, 0)
      .or(isPremarket, item?.data?.ACVOL_PRE)
      .otherwise(item?.data?.ACVOL_1)
      .get() || undefined;

  const changeNominal = when(isPremarket, item?.data?.NCHNG_PRE)
    .otherwise(item?.data?.NETCHNG_1)
    .get();
  const changePercentage = when(isPremarket, item?.data?.PCHNG_PRE)
    .otherwise(item?.data?.PCTCHNG)
    .get();

  return {
    averagePrice: vwap,
    avarage10DayVolume: item?.data?.VOL10DAVG,
    average30DayVolume: item?.data?.VMA_30D,
    average3MonthVolume: item?.data?.VMA_90D,
    numberOfMoves: item?.data?.NUM_MOVES,
    lowDaily: item?.data?.LOW_1,
    highDaily: item?.data?.HIGH_1,
    high52Week,
    low52Week,
    lowDate52Week,
    highDate52Week,
    middlePrice,
    preMarketPrice: item?.data?.PREMKT_PRC,
    betIndicativePrice: item?.data?.THEO_PRC,
    volumeQuantity,
    volumeNominal: (item?.data?.ACVOL_1 && vwap && item?.data?.ACVOL_1 * vwap) || undefined,
    yearToDateChange,
    changeNominal,
    changeNominalRaw: item?.data?.NETCHNG_1,
    changeNominalPreviousRaw: item?.data?.NETCHNG_PREVIOUS,
    changePercentage: isDefined(changePercentage) ? changePercentage / 100 : undefined,
    changePercentageRaw: isDefined(item?.data?.PCTCHNG) ? item?.data?.PCTCHNG / 100 : undefined,
    changePercentagePreviousRaw: isDefined(item?.data?.PCTCHNG_PREVIOUS)
      ? item?.data?.PCTCHNG_PREVIOUS / 100
      : undefined,
  };
};
